import React, { useEffect, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Student } from "../../../../../interfaces/User";
import { useForm } from "react-hook-form";
import Input from "../../../../../design-system-components/Input/Input";

import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { commonMessages } from "../../../../../utils/messages";
import { fullName } from "../../../../../utils/format";
import MiniLoader from "../../../../../components/Loader/MiniLoader";
import { useEditStudentMutation } from "../../api/mutations";

import "./EditStudentForm.scss";

type FormValues = {
    firstname: string;
    lastname: string;
};

type FormProps = {
    student: Student;
    onClose: () => void;
    classroomId: string;
};

const EditStudentForm = ({ student, onClose, classroomId }: FormProps) => {
    const intl = useIntl();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: {
            lastname: student.last_name,
            firstname: student.first_name,
        },
    });

    const lastNameInputRef = useRef<HTMLInputElement | null>(null);
    const { ref, ...lastNameRegistration } = register("lastname", {
        required: true,
    });
    useEffect(() => {
        lastNameInputRef.current?.focus();
    }, []);

    const { mutate, isLoading } = useEditStudentMutation(
        classroomId,
        student.id,
        {
            onSuccess: () => {
                onClose();
            },
        }
    );
    const submitStudentEdit = async (data: FormValues) => {
        if (data.firstname || data.lastname) {
            const payload = {
                first_name: data.firstname
                    ? data.firstname
                    : student.first_name,
                last_name: data.lastname ? data.lastname : student.last_name,
            };
            mutate(payload);
        }
    };

    return (
        <form
            className="edit-student-form"
            onSubmit={handleSubmit((data) => submitStudentEdit(data))}
            aria-label={intl.formatMessage(messages.formtitle, {
                name: fullName(student),
            })}
        >
            <div className="edit-student-form__input-group">
                <VisuallyHidden>
                    <label htmlFor="edit-last-name">
                        {intl.formatMessage(messages.editFirstNameLabel)}
                    </label>
                </VisuallyHidden>
                <Input
                    required
                    id="edit-last-name"
                    error={
                        errors.lastname?.type === "required"
                            ? intl.formatMessage(messages.editLastNameError)
                            : undefined
                    }
                    {...lastNameRegistration}
                    ref={(e) => {
                        ref(e);
                        lastNameInputRef.current = e;
                    }}
                />
                <VisuallyHidden>
                    <label htmlFor="edit-first-name">
                        {intl.formatMessage(messages.editFirstNameLabel)}
                    </label>
                </VisuallyHidden>
                <Input
                    id="edit-first-name"
                    required
                    error={
                        errors.firstname?.type === "required"
                            ? intl.formatMessage(messages.editfirstNameError)
                            : undefined
                    }
                    {...register("firstname", { required: true })}
                />
            </div>
            <div className="edit-student-form__action-buttons">
                {isLoading && <MiniLoader color={"var(--element-grey20)"} />}
                {!isLoading && (
                    <>
                        <button
                            type="submit"
                            className="student-name__submit"
                            onClick={handleSubmit((data) =>
                                submitStudentEdit(data)
                            )}
                        >
                            <Icon path="checkmark" />
                            <VisuallyHidden>
                                {intl.formatMessage(commonMessages.submit)}
                            </VisuallyHidden>
                        </button>
                        <button
                            className="student-name__cancel"
                            onClick={onClose}
                        >
                            <Icon path="close" size="medium" />
                            <VisuallyHidden>
                                {intl.formatMessage(commonMessages.cancel)}
                            </VisuallyHidden>
                        </button>
                    </>
                )}
            </div>
        </form>
    );
};

export default EditStudentForm;

const messages = defineMessages({
    formtitle: {
        id: "student-manager-editStudentFormTitle",
        defaultMessage: "Edit {name}",
    },
    editFirstNameLabel: {
        id: "studentManager-editFirstNameLabel",
        defaultMessage: "Edit first name",
    },
    editLastNameLabel: {
        id: "studentManager-editLastNameLabel",
        defaultMessage: "Edit last name",
    },
    editLastNameError: {
        id: "studentManager-editLastNameError",
        defaultMessage: "Last name is required",
    },
    editfirstNameError: {
        id: "studentManager-editfirstNameError",
        defaultMessage: "First name is required",
    },
});
